import { action, computed, makeAutoObservable, observable } from "mobx";
import Store from "./Store";
import { WorkoutData } from "./WorkoutStore";
import dayjs, { Dayjs } from "dayjs";

export default class CopyStore {
  copiedWorkouts: WorkoutData[] = [];
  isMultiCopyMode: boolean = false;
  selectedDates: Set<string> = new Set();
  selectionAnchorKey: string | null = null;
  isDeleting: boolean = false;

  // Derived selections
  get selectedDatesList(): Dayjs[] {
    return Array.from(this.selectedDates.values()).map((k) => dayjs(k));
  }

  get workoutsInSelection(): WorkoutData[] {
    const selectedKeys = this.selectedDates;
    if (selectedKeys.size === 0) return [];
    return this.store.workoutStore.items.filter((item) =>
      selectedKeys.has(dayjs(item.startDate).format("YYYY-MM-DD"))
    );
  }

  get planWorkoutsInSelection(): WorkoutData[] {
    if (this.selectedDates.size === 0) return [];
    const result: WorkoutData[] = [];
    for (const d of this.selectedDatesList) {
      const items = this.store.workoutStore.getPlanWorkouts(d);
      if (items.length > 0) {
        result.push(...items);
      }
    }
    return result;
  }

  get hasAnyInSelection(): boolean {
    return this.workoutsInSelection.length > 0;
  }

  get hasPlanInSelection(): boolean {
    return this.planWorkoutsInSelection.length > 0;
  }

  setCopiedWorkoutsFromSelection() {
    // Copy only plan workouts from selected days (like single-day copy)
    const dates = this.selectedDatesList.sort((a, b) => a.valueOf() - b.valueOf());
    if (dates.length === 0) {
      this.clearCopiedWorkouts();
      return;
    }
    const base = dates[0];
    const result: WorkoutData[] = [];

    dates.forEach((d) => {
      const offsetDays = d.diff(base, "day");
      const items = this.store.workoutStore.getPlanWorkouts(d);
      items.forEach((workout) => {
        const copied = {
          id: undefined as any,
          athleteId: workout.athleteId,
          name: workout.name,
          startDate: null as any, // will be set during paste
          type: workout.type,
          description: workout.description,
          target: workout.target,
          plan: workout.plan,
          fact: "", // copy only plan
          createdEmpty: workout.createdEmpty,
          // meta (not sent to server; api explicitly picks fields)
          __fromMultiCopy: true,
          __offsetDays: offsetDays,
        } as any;
        result.push(copied);
      });
    });

    this.copiedWorkouts = result;
  }

  private getDateKey(date: Dayjs): string {
    return dayjs(date).format("YYYY-MM-DD");
  }

  setCopiedWorkouts(workouts: WorkoutData[]) {
    // Фильтруем и сохраняем только нужные поля
    this.copiedWorkouts = workouts.map((workout) => ({
      id: undefined, // id не копируем, будет создан новый при вставке
      athleteId: workout.athleteId,
      name: workout.name,
      startDate: null as any, // startDate не копируем, будет установлена при вставке
      type: workout.type,
      description: workout.description,
      target: workout.target,
      plan: workout.plan,
      fact: "", // copy only plan
      createdEmpty: workout.createdEmpty,
    }));
  }

  clearCopiedWorkouts() {
    this.copiedWorkouts = [];
  }

  get hasCopiedWorkouts(): boolean {
    return this.copiedWorkouts.length > 0;
  }

  setMultiCopyMode(active: boolean) {
    this.isMultiCopyMode = active;
    if (!active) {
      this.clearSelectedDates();
      this.clearSelectionAnchor();
    }
  }

  toggleSelectedDate(date: Dayjs) {
    const key = this.getDateKey(date);
    if (this.selectedDates.has(key)) {
      this.selectedDates.delete(key);
    } else {
      this.selectedDates.add(key);
    }
  }

  clearSelectedDates() {
    this.selectedDates.clear();
  }

  isDateSelected(date: Dayjs): boolean {
    const key = this.getDateKey(date);
    return this.selectedDates.has(key);
  }

  setSelectionAnchor(date: Dayjs) {
    this.selectionAnchorKey = this.getDateKey(date);
  }

  clearSelectionAnchor() {
    this.selectionAnchorKey = null;
  }

  selectRangeTo(date: Dayjs) {
    if (!this.selectionAnchorKey) {
      // No anchor yet: initialize anchor and select this date
      this.setSelectionAnchor(date);
      const key = this.getDateKey(date);
      this.selectedDates.add(key);
      return;
    }
    const anchor = dayjs(this.selectionAnchorKey);
    const target = dayjs(date);

    const from = anchor.isBefore(target, "day") ? anchor : target;
    const to = anchor.isBefore(target, "day") ? target : anchor;

    // Collect all keys in range
    const rangeKeys: string[] = [];
    let cursor = from;
    while (cursor.isBefore(to, "day") || cursor.isSame(to, "day")) {
      rangeKeys.push(this.getDateKey(cursor));
      cursor = cursor.add(1, "day");
    }

    const anchorKey = this.selectionAnchorKey;
    const keysExcludingAnchor = rangeKeys.filter((k) => k !== anchorKey);

    // Decision rule per spec:
    // - If there exists any unselected day in the range excluding the anchor,
    //   then SELECT the whole range including the anchor.
    // - If all days in the range excluding the anchor are already selected,
    //   then DESELECT the whole range including the anchor.
    const existsUnselectedBeyondAnchor = keysExcludingAnchor.some(
      (k) => !this.selectedDates.has(k)
    );

    if (existsUnselectedBeyondAnchor) {
      rangeKeys.forEach((k) => this.selectedDates.add(k));
    } else {
      rangeKeys.forEach((k) => this.selectedDates.delete(k));
    }

    // Move anchor to the last clicked target to allow chaining
    this.setSelectionAnchor(target);
  }

  constructor(private store: Store) {
    makeAutoObservable(this, {
      setCopiedWorkouts: action.bound,
      clearCopiedWorkouts: action.bound,
      copiedWorkouts: observable,
      isDeleting: observable,
      hasCopiedWorkouts: computed,
      isMultiCopyMode: observable,
      setMultiCopyMode: action.bound,
      selectedDates: observable.shallow,
      toggleSelectedDate: action.bound,
      clearSelectedDates: action.bound,
      selectionAnchorKey: observable,
      setSelectionAnchor: action.bound,
      clearSelectionAnchor: action.bound,
      selectRangeTo: action.bound,
      selectedDatesList: computed,
      workoutsInSelection: computed,
      planWorkoutsInSelection: computed,
      hasAnyInSelection: computed,
      hasPlanInSelection: computed,
    });
  }
}

